import { useMemo } from 'react';
import { addDays, format } from 'date-fns';
import { useFetchSfQuery } from './salesforce-hooks';
import type { FiscalPeriod } from '../support/opportunityDatePresets';

const SF_DATE = 'yyyy-MM-dd';
const NEXT_QUARTER_HORIZON_DAYS = 200;
const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;

const periodsSoql = (today: Date) =>
  `SELECT Type, StartDate, EndDate FROM Period WHERE Type IN ('Quarter', 'Year') ` +
  `AND EndDate >= ${format(today, SF_DATE)} ` +
  `AND StartDate <= ${format(addDays(today, NEXT_QUARTER_HORIZON_DAYS), SF_DATE)} ` +
  `ORDER BY StartDate`;

const toRecords = (data: unknown): any[] => (Array.isArray(data) ? data : []);

/**
 * What the Opportunities date presets need from Salesforce: its fiscal quarters and years, so
 * "Current Quarter" and "Current Fiscal Year" match Salesforce's own.
 */
export const useOpportunityPresetMetadata = () => {
  const periodsQuery = useFetchSfQuery(periodsSoql(new Date()), { staleTime: TWELVE_HOURS_MS });

  const periods: FiscalPeriod[] = useMemo(
    () =>
      toRecords(periodsQuery.data)
        .filter((r) => (r?.Type === 'Quarter' || r?.Type === 'Year') && r?.StartDate && r?.EndDate)
        .map((r) => ({ type: r.Type, start: String(r.StartDate), end: String(r.EndDate) })),
    [periodsQuery.data]
  );

  return {
    periods,
    periodsError: periodsQuery.isError,
    isLoading: periodsQuery.isLoading,
  };
};
